import HUI from "@henge-inc/ui/v1_2";
import { VaultObjectViewerData } from "@/components/vault/viewer/VaultObjectViewerModal";
import { useScreenStore } from "@/stores/useScreenStore";
import useVaultStore, {
  useVaultStoreActions,
} from "@/stores/zustand/vault/useVaultStore";
import { License } from "@/types/enums";
import { motion } from "motion/react";
import { RefObject, useEffect, useRef, useState } from "react";
import { TabButtons } from "@/components/vault/viewer/stickers/tabs/TabButtons";
import { MixerVerticalIcon } from "@radix-ui/react-icons";

interface TabStepsProps {
  vaultObjectViewerData: VaultObjectViewerData | null;
  newName: string;
  newNameInputRef: RefObject<HTMLInputElement | null>;
  setNewNameInputErrorMessage: (value: string) => void;
  newTagValueList: string[];
  newTagInputRef: RefObject<HTMLInputElement | null>;
  setNewTagInputErrorMessage: (value: string) => void;
  newDownloadable: boolean;
  newLicense: HUI.SelectItemInterface<License> | null;
  onCloseModal: () => void;
}

export const TabSteps = ({
  vaultObjectViewerData,
  newName,
  newNameInputRef,
  setNewNameInputErrorMessage,
  newTagValueList,
  newTagInputRef,
  setNewTagInputErrorMessage,
  newDownloadable,
  newLicense,
  onCloseModal,
}: TabStepsProps) => {
  const isMobileScreen = useScreenStore((state) => state.isMobileScreen);

  useEffect(() => {
    if (isMobileScreen) {
      setToggleInspector(false);
    }
  }, [isMobileScreen]);

  const toggleInspector = useVaultStore((state) => state.toggleInspector);
  const { setToggleInspector } = useVaultStoreActions();

  const currentPage = useVaultStore((state) => state.currentPage);
  const { setCurrentTab } = useVaultStore((state) => state.vaultStoreActions);

  useEffect(() => {
    if (toggleInspector) {
      setCurrentTab("show-window");
    } else {
      setCurrentTab("metadata");
    }
  }, [toggleInspector]);

  const clickCopyLinkTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const [clickCopyLink, setClickCopyLink] = useState(false);
  useEffect(() => {
    if (!clickCopyLink) return;

    setClickCopyEmbed(false);

    clearTimeout(clickCopyLinkTimer.current);
    clickCopyLinkTimer.current = setTimeout(() => {
      setClickCopyLink(false);
    }, 1500);

    return () => clearTimeout(clickCopyLinkTimer.current);
  }, [clickCopyLink]);
  const clickCopyEmbedTimer = useRef<NodeJS.Timeout | undefined>(undefined);
  const [clickCopyEmbed, setClickCopyEmbed] = useState(false);
  useEffect(() => {
    if (!clickCopyEmbed) return;

    setClickCopyLink(false);

    clearTimeout(clickCopyEmbedTimer.current);
    clickCopyEmbedTimer.current = setTimeout(() => {
      setClickCopyEmbed(false);
    }, 1500);

    return () => clearTimeout(clickCopyEmbedTimer.current);
  }, [clickCopyEmbed]);

  if (isMobileScreen) return null;
  return (
    <div
      style={{
        bottom: isMobileScreen && !vaultObjectViewerData?.embed ? 20 : 12,
        right: isMobileScreen && !vaultObjectViewerData?.embed ? 20 : 12,
        transition: "bottom 0.3s, right 0.3s",
      }}
      className="absolute pointer-events-auto flex-shrink-0 flex justify-end items-center gap-2"
    >
      <TabButtons
        newName={newName}
        setNewNameInputErrorMessage={setNewNameInputErrorMessage}
        newTagValueList={newTagValueList}
        setNewTagInputErrorMessage={setNewTagInputErrorMessage}
        newLicense={newLicense}
        onCloseModal={onCloseModal}
      />

      {currentPage === "viewer" && (
        <>
          <div className="w-px h-4 bg-[var(--border-tertiary)] flex-shrink-0 mx-2" />
          <motion.div
            layout
            initial={{ opacity: 0, x: 0 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="h-10 w-[68px] rounded-full flex p-1 cursor-pointer transition-colors"
            onClick={() => setToggleInspector(!toggleInspector)}
            style={{
              backgroundColor: toggleInspector
                ? "var(--accent-8)"
                : "var(--gray50-alpha60)",
            }}
          >
            <motion.div
              layout
              className="bg-[var(--white)] h-full w-[32px] rounded-full flex items-center justify-center"
              animate={{
                x: toggleInspector ? 28 : 0,
              }}
              transition={{
                type: "spring",
                stiffness: 500,
                damping: 30,
              }}
            >
              <MixerVerticalIcon />
            </motion.div>
          </motion.div>
        </>
      )}
    </div>
  );
};
